import { useState } from "react";
import { GiCancel, GiPartyPopper } from "react-icons/gi";
import {
  useCancelEvent,
  useEventTemplates,
  useEvents,
  useLaunchTemplate,
} from "../api/events";
import { Loading } from "../pages/Loading";
import { ErrorView } from "../pages/ErrorView";
import { EventEntry, EventTemplate } from "../types/events";
import { useNow } from "../lib/ticker";
import { formatDuration, secondsUntil } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";

interface Props {
  enabled: boolean;
  isTestWorld: boolean;
}

export function EventsView({ enabled, isTestWorld }: Props) {
  const events = useEvents(enabled);
  const templates = useEventTemplates(enabled && isTestWorld);
  const launch = useLaunchTemplate();
  const cancel = useCancelEvent();
  const now = useNow(1000);
  const [picker, setPicker] = useState(false);

  if (events.isPending) return <Loading message="События загружаются..." />;
  if (events.isError) return <ErrorView message="Не получилось загрузить события." />;

  const data = events.data!;
  const active = data.events.filter((e) => e.active);
  const upcoming = data.events.filter((e) => !e.active);

  const errorMsg = pickError(launch.error) ?? pickError(cancel.error);

  return (
    <div className="space-y-3 px-3 pb-3">
      {data.activeBonuses.length > 0 && (
        <div className="rounded-xl bg-tg-secondaryBg p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
            Сейчас действуют бонусы
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.activeBonuses.map((b) => (
              <span
                key={b.kind}
                className="rounded-full bg-tg-bg px-2.5 py-0.5 text-xs"
              >
                {b.kindName}: +{Math.round(b.totalValue * 100)}%
              </span>
            ))}
          </div>
        </div>
      )}

      {isTestWorld && (
        <button
          type="button"
          onClick={() => {
            hapticImpact("light");
            setPicker(true);
          }}
          className="w-full rounded-lg bg-tg-button px-4 py-2.5 text-sm font-medium text-tg-buttonText"
        >
          <span className="inline-flex items-center justify-center gap-1.5"><GiPartyPopper size={14} /> Запустить событие</span>
        </button>
      )}

      {errorMsg && (
        <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {errorMsg}
        </div>
      )}

      {active.length > 0 && (
        <Section title="Активные">
          <div className="space-y-2">
            {active.map((e) => (
              <EventCard
                key={e.id}
                ev={e}
                now={now}
                isTestWorld={isTestWorld}
                onCancel={() => {
                  if (confirm(`Отменить «${e.name}»?`)) {
                    cancel.mutate(e.id);
                    hapticNotification("success");
                  }
                }}
                cancelPending={cancel.isPending}
              />
            ))}
          </div>
        </Section>
      )}

      {upcoming.length > 0 && (
        <Section title="Предстоят">
          <div className="space-y-2">
            {upcoming.map((e) => (
              <EventCard
                key={e.id}
                ev={e}
                now={now}
                isTestWorld={isTestWorld}
                onCancel={() => {
                  if (confirm(`Отменить «${e.name}»?`)) cancel.mutate(e.id);
                }}
                cancelPending={cancel.isPending}
              />
            ))}
          </div>
        </Section>
      )}

      {active.length === 0 && upcoming.length === 0 && (
        <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
          Событий нет.
          {isTestWorld && (
            <div className="mt-1">Запусти любой шаблон выше.</div>
          )}
        </div>
      )}

      {picker && (
        <TemplatePicker
          templates={templates.data ?? []}
          onPick={(t) => {
            const startsAt = new Date().toISOString();
            launch.mutate(
              { templateId: t.id, startsAt },
              {
                onSuccess: () => {
                  hapticNotification("success");
                  setPicker(false);
                },
              },
            );
          }}
          onClose={() => setPicker(false)}
          pending={launch.isPending}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">{title}</div>
      {children}
    </section>
  );
}

function EventCard({
  ev,
  now,
  isTestWorld,
  onCancel,
  cancelPending,
}: {
  ev: EventEntry;
  now: Date;
  isTestWorld: boolean;
  onCancel: () => void;
  cancelPending: boolean;
}) {
  const remaining = ev.active
    ? secondsUntil(ev.endsAt, now)
    : secondsUntil(ev.startsAt, now);
  return (
    <div className="rounded-xl bg-tg-secondaryBg p-3">
      <div className="flex items-start gap-3">
        <div className="text-3xl">{ev.icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="truncate font-medium">{ev.name}</div>
            <div className="text-xs tabular-nums text-tg-accent">
              {ev.active ? "⏳" : "🕒"} {formatDuration(remaining)}
            </div>
          </div>
          {ev.description && (
            <div className="mt-0.5 line-clamp-2 text-xs text-tg-hint">
              {ev.description}
            </div>
          )}
          <div className="mt-2 space-y-0.5">
            {ev.modifiers.map((m, i) => (
              <div key={i} className="text-xs text-tg-hint">
                {m.display}
              </div>
            ))}
          </div>
        </div>
      </div>
      {isTestWorld && (
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelPending}
          className="mt-2 w-full rounded-lg bg-tg-bg px-3 py-1.5 text-xs text-tg-destructive disabled:opacity-40"
        >
          <span className="inline-flex items-center justify-center gap-1.5"><GiCancel size={12} /> Отменить</span>
        </button>
      )}
    </div>
  );
}

function TemplatePicker({
  templates,
  onPick,
  onClose,
  pending,
}: {
  templates: EventTemplate[];
  onPick: (t: EventTemplate) => void;
  onClose: () => void;
  pending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-10 flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full overflow-y-auto rounded-t-2xl bg-tg-bg p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="text-lg font-semibold">Шаблоны событий</div>
          <button type="button" onClick={onClose} className="text-tg-hint">
            ✕
          </button>
        </div>
        <div className="space-y-2">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={pending}
              onClick={() => onPick(t)}
              className="flex w-full items-start gap-3 rounded-xl bg-tg-secondaryBg p-3 text-left disabled:opacity-50"
            >
              <div className="text-3xl">{t.icon}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-tg-hint">{t.durationHours}ч</div>
                </div>
                {t.description && (
                  <div className="mt-0.5 text-xs text-tg-hint">{t.description}</div>
                )}
                <div className="mt-1 space-y-0.5">
                  {t.modifiers.map((m, i) => (
                    <div key={i} className="text-xs">
                      {m.display}
                    </div>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function pickError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const resp = (err as { response?: { data?: { error?: string } } }).response;
  return resp?.data?.error ?? null;
}
